// The floating origin: what lets the picture hold together far from where the world's numbers start.
//
// The world is 512 km across and periodic, and the eye can be anywhere in it. A 32 bit float holds 24 bits of mantissa: at 250 km from the origin the step between two
// neighbouring numbers is 1.6 cm, at 512 km 3 cm, and a shader that adds an eye position of that size to a ray, or looks a wave tile up at p / L, or hashes a 6 cm noise cell, shows it: the
// water's fine ripples and the foam's lace swim in steps, the rain's rings (4 cm wide) are noise. Three itself is not the problem (it builds the model-view matrices in doubles on
// the CPU, relative to the camera, so a mesh and its vertices are always exact); the shaders that take the eye's position, or the world position of a pixel, as a float are.
//
// The cure is to count from a point near the eye. O (x, z; y is not shifted: the sea level, the clouds and the air are in heights above the ground, which stay small) is a lattice point of
// ORIGIN_STEP_M, so a position relative to it, P - O, is small and exact, and the lookups that need the whole position (the terrain field, the wave tiles' phase, the cloud rings' windows,
// the noise frames) add O back where it is exact, from numbers computed here in doubles: an offset modulo the period of the thing it is added to (O mod L for a tile of side L, O times the
// scale for a hash's cells, and so on). The lattice is what keeps the picture from jumping when O changes: ORIGIN_STEP_M is a power of two, so O times any scale the shaders use that is a
// short decimal is a whole number of cells, and a hash cell has the same index before and after; every other offset is a function of O alone, so the same point is looked up at the same place.
//
// Near the island (within ORIGIN_NEAR_M of the world origin) O is 0 and the shaders compute exactly what they did before, bit for bit. Further out it sits at the lattice point nearest the
// eye with a margin (it moves when the eye is more than three quarters of a step from it), so it changes seldom and never flickers between two neighbours.
//
// ?origin=off keeps O at 0 (the old behaviour, to compare), ?origin=force uses the lattice everywhere (to test it near the island).
import * as THREE from 'three';

export const ORIGIN_STEP_M = 4096;
export const ORIGIN_NEAR_M = 8192;          // O stays 0 while the eye is within this of the world origin (in x and z)
const NEAR_BACK_M = ORIGIN_NEAR_M * 0.75;   // ... and returns to 0 within this (so it does not flicker on the edge)
const HYST = 0.75;                           // the eye must be this many steps from O (in x or z) before O moves
const CELLS_HALF = 2097152;                  // 2^21: a hash's cell index is kept in [-2^21, 2^21) (the shaders do the same), exactly representable in a float

export type OriginMode = 'auto' | 'off' | 'force';

export function parseOriginMode(s: string | null | undefined): OriginMode {
  if (s === 'off' || s === '0') return 'off';
  if (s === 'force' || s === 'on') return 'force';
  return 'auto';
}

const mod = (a: number, n: number): number => ((a % n) + n) % n;

export class Origin {
  /** O (x, z), m: a lattice point (floats hold it exactly). The shared uniform uOriginXZ. */
  readonly org = new THREE.Vector2(0, 0);
  /** The eye relative to O (x, y, z): y is the eye's own height. The shared uniform uEyeRel. */
  readonly eye = new THREE.Vector3();
  /** Counts the times O has changed (hosts that keep numbers relative to it redo them). */
  version = 0;
  mode: OriginMode = 'auto';
  /** True while pin() holds O where it was put (a test: update does not move it). */
  pinned = false;
  /** The uniforms every shader that counts from O shares (all-in-one object: add it to the material's own). */
  readonly uniforms: Record<string, THREE.IUniform> = {
    uOriginXZ: { value: this.org },
    uEyeRel: { value: this.eye },
  };

  setMode(m: OriginMode): void {
    if (m === this.mode) return;
    this.mode = m;
    this.pinned = false;
    if (this.org.x !== 0 || this.org.y !== 0) this.org.set(0, 0);
    this.version++;
  }

  /** Hold O at (x, z) whatever the eye does (the tests use it); unpin() lets update move it again. */
  pin(x: number, z: number): void {
    this.pinned = true;
    if (this.org.x !== x || this.org.y !== z) { this.org.set(x, z); this.version++; }
  }
  unpin(): void { this.pinned = false; }

  /** The eye is at (x, y, z) in the world: move O if it should move, and set the eye relative to it. Returns true when O changed. */
  update(x: number, y: number, z: number): boolean {
    let ox = this.org.x, oz = this.org.y;
    if (this.pinned) { /* held */ }
    else if (this.mode === 'off') { ox = 0; oz = 0; }
    else {
      const zero = ox === 0 && oz === 0;
      const m = Math.max(Math.abs(x), Math.abs(z));
      if (this.mode === 'auto' && m < (zero ? ORIGIN_NEAR_M : NEAR_BACK_M)) { ox = 0; oz = 0; }
      else if (zero || Math.abs(x - ox) > HYST * ORIGIN_STEP_M || Math.abs(z - oz) > HYST * ORIGIN_STEP_M) {
        ox = Math.round(x / ORIGIN_STEP_M) * ORIGIN_STEP_M;
        oz = Math.round(z / ORIGIN_STEP_M) * ORIGIN_STEP_M;
      }
    }
    const changed = ox !== this.org.x || oz !== this.org.y;
    if (changed) { this.org.set(ox, oz); this.version++; }
    this.eye.set(x - ox, y, z - oz);
    return changed;
  }

  /** The eye of some other camera (a probe, a march target) relative to O. */
  relTo(x: number, y: number, z: number, out: THREE.Vector3): THREE.Vector3 {
    return out.set(x - this.org.x, y, z - this.org.y);
  }

  /** O modulo a period (m), each axis in [0, period): the offset that takes a position counted from O to the same place on a tile or window of that period. */
  modulo(period: number, out: THREE.Vector2): THREE.Vector2 {
    return out.set(mod(this.org.x, period), mod(this.org.y, period));
  }

  /**
   * O in cells of a grid of cell size 1/scale m (a hash's lattice: wp * scale is the cell coordinate), as whole numbers in [-2^21, 2^21) (O times the scale is a whole number whenever
   * scale is a short decimal and O a multiple of 4096, so nothing is lost). The shader adds it to the cell index of the relative position and takes the sum into the same range, so the
   * index a hash sees is the absolute one (wrapped, for a point more than 2^21 cells out), the same before and after O moves, and the same as with O at 0.
   */
  cells(scale: number, out: THREE.Vector2): THREE.Vector2 {
    const w = (v: number): number => mod(Math.round(v * scale) + CELLS_HALF, 2 * CELLS_HALF) - CELLS_HALF;
    return out.set(w(this.org.x), w(this.org.y));
  }
}

/** The one origin the renderer shares (like the planet). */
export const origin = new Origin();
